import React from 'react';
import ReactDOM from 'react-dom/client';
import AppRoot from './App.jsx';
import AppErrorBoundary from './components/AppErrorBoundary.jsx';
import { AuthProvider } from './contexts/AuthContext.jsx';
import { Toaster } from '@/components/ui/toaster';
import CookieConsentBanner from './components/CookieConsentBanner.jsx';
import { notify } from './lib/notifications.js';
import { api } from './services/api/client.js';
import { startUxTracker } from './utils/uxTracker.js';
import { initFunnelTracker } from './lib/funnel.js';
import { initClarity } from './lib/clarity.js';
import { initAdsUserData } from './lib/gadsUserData.js';
import { capturePlanIntent } from './lib/planIntent.js';
import { captureAttributionHandoff } from './lib/acquisitionAttribution.js';
import './i18n';

// Remove the consent-approved marketing handoff before telemetry can capture
// its query. Other query parameters and the OAuth return remain untouched.
captureAttributionHandoff();

// UX telemetry (TASK-071): reconstructable sessions — referrer/UTM, clicks,
// mouse @2Hz, route changes. Fail-silent by design; AuthContext attaches the
// user id once the session is known.
try {
  startUxTracker({ apiBase: api.baseURL, app: 'app' });
} catch { /* telemetry must never block boot */ }

// Funnel-event tracker (TASK-122, issue #214) — DARK by default behind
// 'dathent.funnel.analytics' (see lib/flags.js); a no-op until that flag is
// on. AuthContext attaches the user id once the session is known.
try {
  initFunnelTracker({ apiBase: api.baseURL });
} catch { /* telemetry must never block boot */ }

// Microsoft Clarity (TASK-122) — DARK by default: only injects if
// 'dathent.clarity.id' (localStorage) or VITE_CLARITY_ID (build env) is
// set. No project id is hardcoded, so this is a no-op out of the box.
try {
  initClarity();
} catch { /* Clarity must never block boot */ }

// Google Ads enhanced conversions (GH-1130): follow the visitor's cookie
// choice ('dathent:consent') from boot, so the hashed email is set before
// any conversion held for that choice is flushed. AuthContext supplies the
// email; nothing is sent without marketing consent.
try {
  initAdsUserData();
} catch { /* ads must never block boot */ }

// TASK-313 — landing's two-track pricing CTAs link in with ?plan=<key>
// (posting/starter/growth/pro/agency). Captured once at boot (every public
// route this needs — /signup, /login, /onboarding, /app/plans — goes through
// a fresh SPA boot) so the intent survives the OAuth/email-verify round
// trips, which are full page reloads. No-op for an absent/unknown key.
try {
  capturePlanIntent(window.location.search);
} catch { /* attribution capture must never block boot */ }

// Global safety net: route any stray window.alert() through the styled toast
// system instead of the browser's native (ugly, blocking) dialog. This catches
// alerts anywhere in the app — current and future — without hunting every call
// site. Most alerts are errors/notices, so default to a "warning" toast; call
// sites that know their exact severity still call notify() directly. (window.confirm
// can't be shimmed the same way — it's synchronous — so those stay per-site dialogs.)
if (typeof window !== 'undefined' && typeof window.alert === 'function') {
  const nativeAlert = window.alert.bind(window);
  window.alert = (message) => {
    try {
      notify({ level: 'warning', title: String(message ?? '').trim() || 'Notice' });
    } catch {
      nativeAlert(message);
    }
  };
}
import './styles/tailwind.css';
import './styles/tokens.css';
import './styles.css';
import './styles-shell.css';
import './styles-dashboard.css';
import './styles-scheduler.css';
import './styles-screens.css';
import './styles-preview.css';
import './styles-content-improvements.css';
import './styles/shell.css';
import './styles/dashboard.css';
import './styles/library.css';
import './styles/post-preview.css';
import './styles/edit-post.css';
import './styles/settings.css';
import './styles/postpay.css';
import './styles/create-post.css';
import './styles/caption-editor.css';
import './styles/content-plan.css';
import './styles/preview-paywall.css';
import './styles/onboarding-nudge.css';
import './styles/pending-oauth.css';
import './styles/product-tour.css';
import './styles/brand-kit.css';
import './styles/video.css';
import './styles/edit-strategy.css';
import './styles/page-header.css';
import './styles/calendar.css';
import './styles/batch.css';
import './styles/auth.css';
import './styles/onboarding.css';
import './styles/onboarding-celebrate.css';
import './styles/onboarding-activation.css';
import './styles/mcp-onboarding.css';
import './styles/dashboard-tour.css';
import './styles/assistant-fab.css';
import './styles/upload-progress.css';
import './styles/assistant.css';
import './styles/error-boundary.css';
import './styles/referrals.css';
import './styles/generation-status.css';
import './styles/upgrade-modal.css';
import './styles/quick-brand.css';
import './styles/plans-page.css';
import './styles/posting-lock.css';
import './styles/free-preview.css';
// Loaded LAST: additive text-overflow safety net (must win the cascade).
import './styles/overflow-safety.css';
import './styles/mobile-navigation.css';
import './styles/cookie-consent.css';

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <AppErrorBoundary>
      <AuthProvider>
        <AppRoot />
        <Toaster />
        {/* GH-1134: cookie consent (Google Consent Mode starts denied in index.html). */}
        <CookieConsentBanner />
      </AuthProvider>
    </AppErrorBoundary>
  </React.StrictMode>
);
